import {useCallback, useState} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
import {
  buildPullRequestUserActionContext,
  type PullRequestUserActionStack,
} from '@github-ui/pull-requests/analytics/pull-request-user-action'

import {useStackRebaseMutation, useStackRebaseStatus} from './mutations/use-stack-rebase-mutation'
import {useMergeBoxPageDataQueryKey} from '../page-data/loaders/use-merge-box-page-data'
import type {StackSectionStackEntry} from '../helpers/merge-box-status-calculator/stack-section-status'

type UseRebaseStackResult = {
  // Triggers a stack rebase, firing the associated analytics.
  onRebaseStack: () => void
  // Error message from the most recent failed rebase, or null.
  rebaseErrorMessage: string | null
  // True while a stack rebase is in progress.
  isRebasing: boolean
  // Number of not-yet-merged entries that a rebase would update.
  outOfDateCount: number
}

/**
 * Encapsulates the stack rebase flow shared by `StackSection` and
 * `StacksMergeConflictsAndRebaseSection`. It owns the rebase mutation, the
 * `stack_rebase_click` / `conflicts_section.update_branch` analytics, and the
 * error message surfaced when a rebase fails, so consuming components only need
 * to render the returned handler, count, and error message.
 */
export function useRebaseStack({
  stackEntries,
  stackMetadata,
}: {
  stackEntries: StackSectionStackEntry[]
  stackMetadata?: PullRequestUserActionStack
}): UseRebaseStackResult {
  const {isRebasing} = useStackRebaseStatus()
  const [rebaseErrorMessage, setRebaseErrorMessage] = useState<string | null>(null)
  const {sendAnalyticsEvent} = useAnalytics()
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const queryClient = useQueryClient()
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()

  const {mutate: rebaseStack} = useStackRebaseMutation({
    onSuccess: async () => {
      await queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
    },
    onError: (e: Error) => {
      setRebaseErrorMessage(e.message)
    },
  })

  const onRebaseStack = useCallback(() => {
    if (isRebasing) return
    setRebaseErrorMessage(null)
    if (stackMetadata) {
      const positionInStack = stackEntries.findIndex(entry => entry.position === 'CURRENT') + 1
      sendClickAnalyticsEvent(
        buildPullRequestUserActionContext({
          category: 'merge_box',
          action: 'stack_rebase_click',
          data: {
            stack: stackMetadata,
            stack_size: stackEntries.length,
            position_in_stack: positionInStack,
          },
        }),
      )
    }
    rebaseStack({entries: stackEntries})
    sendAnalyticsEvent('conflicts_section.update_branch', 'MERGEBOX_CONFLICTS_SECTION_UPDATE_BRANCH_BUTTON')
  }, [isRebasing, stackEntries, stackMetadata, rebaseStack, sendAnalyticsEvent, sendClickAnalyticsEvent])

  const outOfDateCount = stackEntries.filter(entry => entry.pull.state !== 'MERGED').length

  return {onRebaseStack, rebaseErrorMessage, isRebasing, outOfDateCount}
}
